import type { NotificationResponse } from '@/types/notification';
import { i18n } from '@/i18n';
import { humanizeColumnKey } from '@/utils/tableCellValue';
import { formatDateTime } from '@/utils/format';

const MESSAGE_KEY_RE = /\b(?:columns|workflow)\.[A-Za-z0-9_.]+\b/g;

/** Resolve legacy backend message keys embedded in stored notification text. */
export function localizeNotificationText(text?: string | null): string {
  const cleaned = cleanNotificationText(text);
  if (!cleaned || !i18n.isInitialized) return cleaned;

  return cleaned.replace(MESSAGE_KEY_RE, (key) => {
    if (key.startsWith('columns.')) {
      const columnKey = key.slice('columns.'.length);
      for (const ns of ['hr', 'common'] as const) {
        const translated = i18n.t(`columns.${columnKey}`, { ns, defaultValue: '' });
        if (translated && translated !== `columns.${columnKey}`) return translated;
      }
      return humanizeColumnKey(columnKey);
    }

    if (key.startsWith('workflow.')) {
      const subKey = key.slice('workflow.'.length);
      const aliasKey = subKey === 'submitted' ? 'status.submitted' : subKey;
      const translated = i18n.t(aliasKey, { ns: 'workflow', defaultValue: '' });
      if (translated && translated !== aliasKey) return translated;
      return humanizeColumnKey(subKey.replace(/\./g, ' '));
    }

    return key;
  });
}

export function cleanNotificationText(text?: string | null): string {
  if (!text?.trim()) return '';

  return text
    .replace(/\s*·\s*(?:HR_[A-Z_]+\s*#?\d+|employee\s*#\d+)\b/gi, '')
    .replace(/\b(?:HR_[A-Z_]+|employee)\s*#\d+\b/gi, '')
    .replace(/\bHR_[A-Z_]+-\d+\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s·\s·\s/g, ' · ')
    .replace(/^\s*·\s*/g, '')
    .replace(/\s*·\s*$/g, '')
    .trim();
}

export function isNotificationUnread(notification: NotificationResponse): boolean {
  return notification.readFlag !== true;
}

export function formatNotificationTime(createdAt?: string): string {
  if (!createdAt) return '—';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '—';

  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 45) {
    return i18n.t('time.justNow', { ns: 'notifications', defaultValue: 'Just now' });
  }
  if (diffMin < 60) {
    return i18n.t('time.minutesAgo', { ns: 'notifications', count: diffMin, defaultValue: `${diffMin}m ago` });
  }
  if (diffHour < 24) {
    return i18n.t('time.hoursAgo', { ns: 'notifications', count: diffHour, defaultValue: `${diffHour}h ago` });
  }
  if (diffDay === 1) {
    return i18n.t('time.yesterday', { ns: 'notifications', defaultValue: 'Yesterday' });
  }
  if (diffDay < 7) return `${diffDay}d ago`;

  return formatDateTime(createdAt);
}

export function countReadNotifications(items: NotificationResponse[]): number {
  return items.filter((n) => !isNotificationUnread(n)).length;
}
